(() => {
  const root = document.documentElement;
  if (root.hasAttribute('data-rpd-deferred-sections-loaded')) return;
  root.setAttribute('data-rpd-deferred-sections-loaded', 'true');

  const SECTION_SELECTOR = '[data-deferred-section]';
  const DEFERRED_IMAGE_SELECTOR = 'img[data-card-deferred-image="true"][data-src]';
  const SECTION_READY_EVENT = 'rpd:deferred-section:ready';
  const HYDRATE_IMAGES_EVENT = 'rpd:deferred-card-images:hydrate';
  const DEFAULT_ROOT_MARGIN = '0px 0px 900px 0px';
  const LAST_VIEWED_STORAGE_KEY = 'last_viewed_products';
  const LAST_VIEWED_TTL_MS = 14 * 24 * 60 * 60 * 1000;
  const SCRIPT_SRCS_ATTR = 'data-deferred-section-script-srcs';
  const RUNTIME_ATTRS = new Set([
    'data-deferred-section-observed',
    'data-deferred-section-ready',
    'data-deferred-section-loading',
    'data-deferred-section-loaded',
  ]);
  const scriptLoadPromises = new Map();

  const markLoaded = (image) => {
    const media = image.closest('.card__media--deferred');
    if (!media) return;
    media.classList.add('is-deferred-image-loaded');
  };

  const hydrateImage = (image) => {
    if (!image) return;

    const handleComplete = () => {
      markLoaded(image);
    };

    image.addEventListener('load', handleComplete, { once: true });
    image.addEventListener('error', handleComplete, { once: true });

    const src = image.getAttribute('data-src');
    const srcset = image.getAttribute('data-srcset');
    const sizes = image.getAttribute('data-sizes');

    if (src) image.setAttribute('src', src);
    if (srcset) image.setAttribute('srcset', srcset);
    if (sizes) image.setAttribute('sizes', sizes);

    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');
    image.removeAttribute('data-sizes');
    image.removeAttribute('data-card-deferred-image');

    if (image.complete) handleComplete();
  };

  const shouldSkipImage = (image, options = {}) => {
    if (options.includeHidden) return false;
    if (image.closest('[hidden]')) return true;
    if (image.closest('[data-collection-hidden]')) return true;
    return false;
  };

  const hydrateDeferredCardImages = (scope, options = {}) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    scope.querySelectorAll(DEFERRED_IMAGE_SELECTOR).forEach((image) => {
      if (shouldSkipImage(image, options)) return;
      hydrateImage(image);
    });
  };

  const dispatchSectionReady = (section) => {
    if (!section || section.dataset.deferredSectionReady === 'true') return;
    section.dataset.deferredSectionReady = 'true';
    section.dispatchEvent(
      new CustomEvent(SECTION_READY_EVENT, {
        bubbles: true,
        detail: { section },
      })
    );
  };

  const parseScriptSrcs = (section) =>
    String(section?.getAttribute(SCRIPT_SRCS_ATTR) || '')
      .split(',')
      .map((src) => src.trim())
      .filter(Boolean);

  const loadScript = (src) => {
    if (!src) return Promise.resolve();
    if (scriptLoadPromises.has(src)) return scriptLoadPromises.get(src);

    const existing = Array.from(document.querySelectorAll('script[src]')).find((script) => script.src === src);
    if (existing) {
      const resolved = Promise.resolve();
      scriptLoadPromises.set(src, resolved);
      return resolved;
    }

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.dataset.rpdDeferredSectionScript = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Unable to load deferred section script: ${src}`));
      document.head.appendChild(script);
    }).catch(() => {});

    scriptLoadPromises.set(src, promise);
    return promise;
  };

  const loadSectionScripts = (section) => {
    const scriptSrcs = parseScriptSrcs(section);
    if (!scriptSrcs.length) return;
    scriptSrcs.forEach((src) => {
      loadScript(src);
    });
  };

  const getStoredLastViewedHandles = () => {
    const now = Date.now();

    try {
      const raw = window.localStorage.getItem(LAST_VIEWED_STORAGE_KEY);
      if (!raw) return '';

      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return '';

      return list
        .filter((item) => item && item.handle && item.viewedAt && now - item.viewedAt <= LAST_VIEWED_TTL_MS)
        .map((item) => String(item.handle).trim())
        .filter(Boolean)
        .slice(0, 20)
        .join(',');
    } catch (error) {
      return '';
    }
  };

  const buildSectionRequestUrl = (section) => {
    const sectionId = section?.dataset?.sectionId || section?.dataset?.deferredSectionId;
    if (!sectionId) return null;

    const requestUrl = new URL(window.location.href);
    requestUrl.searchParams.delete('section_id');
    requestUrl.searchParams.set('sections', sectionId);
    requestUrl.searchParams.set('rpd_deferred_content', '1');

    const lastViewedHandles = getStoredLastViewedHandles();
    if (lastViewedHandles) {
      if (!requestUrl.searchParams.has('rpd_lvh')) {
        requestUrl.searchParams.set('rpd_lvh', lastViewedHandles);
      }

      if (
        (section.hasAttribute('data-last-viewed-section') || section.dataset.deferredSectionIncludeLastViewed === 'true') &&
        !requestUrl.searchParams.has('lv_handles')
      ) {
        requestUrl.searchParams.set('lv_handles', lastViewedHandles);
      }
    }

    return requestUrl;
  };

  const getFetchedSectionRoot = (markup, section) => {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const sectionId = section?.dataset?.deferredSectionId || section?.dataset?.sectionId;

    return (
      (sectionId ? doc.querySelector(`[data-deferred-section-id="${sectionId}"]`) : null) ||
      doc.body.firstElementChild
    );
  };

  const getFetchedSectionMarkup = (payload, section) => {
    const sectionId = section?.dataset?.deferredSectionId || section?.dataset?.sectionId;
    const trimmedPayload = String(payload || '').trim();

    if (!trimmedPayload.startsWith('{')) return payload;

    try {
      const sections = JSON.parse(trimmedPayload);
      if (sectionId && sections[sectionId]) return sections[sectionId];
      return Object.values(sections).find((markup) => typeof markup === 'string' && markup.trim());
    } catch (error) {
      return payload;
    }
  };

  const syncFetchedSection = (currentSection, nextSection) => {
    if (!currentSection || !nextSection) return null;

    if (currentSection.tagName !== nextSection.tagName) {
      nextSection.removeAttribute('data-deferred-section-fetch');
      nextSection.removeAttribute('data-deferred-section-loading');
      nextSection.setAttribute('data-deferred-section-loaded', 'true');
      currentSection.replaceWith(nextSection);
      return nextSection;
    }

    currentSection.className = nextSection.className;

    if (nextSection.id) {
      currentSection.id = nextSection.id;
    } else {
      currentSection.removeAttribute('id');
    }

    if (nextSection.hasAttribute('style')) {
      currentSection.setAttribute('style', nextSection.getAttribute('style'));
    } else {
      currentSection.removeAttribute('style');
    }

    Array.from(nextSection.attributes).forEach((attribute) => {
      if (attribute.name === 'class' || attribute.name === 'id' || attribute.name === 'style') return;
      if (RUNTIME_ATTRS.has(attribute.name)) return;
      currentSection.setAttribute(attribute.name, attribute.value);
    });

    currentSection.removeAttribute('data-deferred-section-fetch');
    currentSection.removeAttribute('data-deferred-section-loading');
    currentSection.setAttribute('data-deferred-section-loaded', 'true');
    currentSection.innerHTML = nextSection.innerHTML;
    return currentSection;
  };

  const fetchSectionContent = async (section) => {
    if (!section || section.dataset.deferredSectionLoaded === 'true') return true;
    if (section._deferredSectionFetchPromise) return section._deferredSectionFetchPromise;

    const requestUrl = buildSectionRequestUrl(section);
    if (!requestUrl) return false;

    section.dataset.deferredSectionLoading = 'true';
    section.setAttribute('aria-busy', 'true');

    section._deferredSectionFetchPromise = fetch(requestUrl.toString(), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load deferred section (${response.status})`);
        }

        return response.text();
      })
      .then((payload) => {
        const markup = getFetchedSectionMarkup(payload, section);
        if (!markup) return false;

        const nextSection = getFetchedSectionRoot(markup, section);
        if (!nextSection) return false;

        return syncFetchedSection(section, nextSection);
      })
      .catch(() => false)
      .finally(() => {
        section.removeAttribute('aria-busy');
        delete section._deferredSectionFetchPromise;
      });

    return section._deferredSectionFetchPromise;
  };

  const activateSection = async (section) => {
    if (!section || section.dataset.deferredSectionReady === 'true') return;

    loadSectionScripts(section);

    let activeSection = section;
    if (section.getAttribute('data-deferred-section-fetch') === 'true') {
      activeSection = await fetchSectionContent(section);
      if (!activeSection) return;
    }

    if (activeSection.hasAttribute('data-deferred-card-section')) {
      hydrateDeferredCardImages(activeSection);
    }

    dispatchSectionReady(activeSection);
  };

  const getRootMargin = (section) => section?.dataset.deferredSectionRootMargin || DEFAULT_ROOT_MARGIN;

  const observeSection = (section) => {
    if (!section || section.dataset.deferredSectionObserved === 'true') return;
    section.dataset.deferredSectionObserved = 'true';

    if (window.Shopify && window.Shopify.designMode) {
      activateSection(section);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      activateSection(section);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0] || !entries[0].isIntersecting) return;
        observer.unobserve(section);
        activateSection(section);
      },
      { rootMargin: getRootMargin(section) }
    );

    observer.observe(section);
  };

  const init = (scope) => {
    if (!scope) return;

    if (scope.matches && scope.matches(SECTION_SELECTOR)) {
      observeSection(scope);
    }

    scope.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => {
      observeSection(section);
    });
  };

  document.addEventListener(HYDRATE_IMAGES_EVENT, (event) => {
    const scope = event.detail?.scope;
    if (!scope) return;
    hydrateDeferredCardImages(scope, {
      includeHidden: event.detail?.includeHidden === true,
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target) return;
    init(event.target);
  });
})();
